/**
 * Bus glyphs for the MBMT live map, drawn with the same minimalist technique as
 * Ultratech's truck marker (console/src/map/layers/markers.ts): a couple of solid
 * colour blocks, chunky tyres, a white outline for contrast against the basemap,
 * and one glass slit that marks the front. That truck reads instantly at 20-40px
 * on screen; earlier bus passes added mullions, hatches, decals, and a dark
 * outline that all turned to mush at the same scale and read as a toy. This drops
 * back to the same handful of shapes truck() uses.
 *
 * Top-down, heading 0 = up the sprite -- a fixed-angle illustration can't be
 * arbitrarily `icon-rotate`d and still look correct from every heading, so this
 * stays a silhouette rather than an isometric drawing. `icon-pitch-alignment:
 * 'viewport'` billboards it on the tilted map. Not SDF -- full-colour ImageData,
 * one per status x model.
 */

export const BUS_MODELS = ['9M', '12M_NAC', '12M_AC'] as const
export type BusModel = (typeof BUS_MODELS)[number]

export const BUS_ICON_SIZE = 256
export const BUS_ICON_PIXEL_RATIO = 2

/** Raw vehicletype string -> icon model key. Unknown/blank types fall back to
 *  the 12M NAC shape so every vehicle always resolves to a registered icon. */
export function busModel(vehicletype: string | null | undefined): BusModel {
  const t = (vehicletype ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (t.includes('9M')) return '9M'
  if (!t.includes('AC') || t.includes('NAC')) return '12M_NAC'
  return '12M_AC'
}

/** Tyres and glass, constant across states -- a bus's wheels are not a status,
 *  same call truck() makes. */
const OUTLINE = 'rgba(255,255,255,0.94)'
const TYRE = '#1F2937'
const GLASS = 'rgba(255,255,255,0.85)'

/** Transparent fallback when no 2d context exists (headless/jsdom): keeps the
 *  size contract map.addImage expects without requiring a global ImageData. */
function blankImageData(size: number): ImageData {
  if (typeof ImageData !== 'undefined') return new ImageData(size, size)
  return {
    width: size,
    height: size,
    data: new Uint8ClampedArray(size * size * 4),
    colorSpace: 'srgb',
  } as ImageData
}

function roundRect(
  g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2)
  g.beginPath()
  g.moveTo(x + rr, y)
  g.lineTo(x + w - rr, y)
  g.quadraticCurveTo(x + w, y, x + w, y + rr)
  g.lineTo(x + w, y + h - rr)
  g.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
  g.lineTo(x + rr, y + h)
  g.quadraticCurveTo(x, y + h, x, y + h - rr)
  g.lineTo(x, y + rr)
  g.quadraticCurveTo(x, y, x, y + rr)
  g.closePath()
}

/** One axle: a tyre either side of the hull, drawn under it so the hull caps them. */
function axle(g: CanvasRenderingContext2D, cx: number, cy: number, halfTrack: number, len: number): void {
  const tw = 8
  g.fillStyle = TYRE
  roundRect(g, cx - halfTrack - tw, cy - len / 2, tw, len, 2.5)
  g.fill()
  roundRect(g, cx + halfTrack, cy - len / 2, tw, len, 2.5)
  g.fill()
}

/**
 * Top-down bus pointing up the sprite (heading 0 = north). One solid body block
 * per status colour, white outline, chunky axle-mounted tyres, a windscreen slit
 * at the nose and a plain neutral window band down each side -- the same shape
 * budget as the truck marker, not more.
 *
 * COLOUR IS STATUS, ONLY STATUS. Model used to tint the AC window band teal and
 * stroke every window in the status edge colour -- a bus reading "moving" (green
 * body) with a teal window band and a darker-green window border was three hues
 * competing on one small glyph, which is what read as "both colours." Model is
 * shape now, exclusively: 9M is a genuine midi (shorter, narrower, two axles);
 * 12M NAC/AC share the long three-axle body, AC told apart by a small neutral
 * roof vent rather than a second colour.
 */
export function busIconImageData(
  fill: string, edge: string, model: BusModel, size = BUS_ICON_SIZE,
): ImageData {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return blankImageData(size)

  const midi = model === '9M'
  const ac = model === '12M_AC'
  const u = size / 256
  const L = (midi ? 150 : 196) * u
  const W = (midi ? 60 : 66) * u
  const cx = size / 2
  const cy = size / 2
  const y0 = cy - L / 2
  const y1 = cy + L / 2
  const halfW = W / 2

  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // ---- running gear, first so the hull overlaps the inner edge of every tyre
  const axles = midi
    ? [y0 + 26 * u, y1 - 24 * u]
    : [y0 + 28 * u, y1 - 46 * u, y1 - 22 * u]
  for (const ay of axles) axle(ctx, cx, ay, halfW - 3 * u, 20 * u)

  // ---- body: one solid block, status colour, white outline -- the truck's cab
  roundRect(ctx, cx - halfW, y0, W, L, 16 * u)
  ctx.fillStyle = fill
  ctx.fill()
  ctx.strokeStyle = OUTLINE
  ctx.lineWidth = 3 * u
  ctx.stroke()

  // ---- one plain window band per side -- neutral glass, same on every model, no
  // second hue competing with the status-coloured body.
  const winTop = y0 + 34 * u
  const winBot = y1 - 20 * u
  for (const side of [-1, 1] as const) {
    const bx = side === 1 ? cx + halfW - 12 * u : cx - halfW + 4 * u
    roundRect(ctx, bx, winTop, 8 * u, winBot - winTop, 3.5 * u)
    ctx.fillStyle = 'rgba(255,255,255,0.4)'
    ctx.fill()
  }

  // ---- windscreen slit at the nose -- the one cue that says which end is front.
  // Edge (a darkened shade of the status colour) borders it, a light touch rather
  // than a second colour block, so status still reads unambiguously from the body.
  roundRect(ctx, cx - (halfW - 16 * u), y0 + 7 * u, W - 32 * u, 9 * u, 3 * u)
  ctx.fillStyle = GLASS
  ctx.fill()
  ctx.strokeStyle = edge
  ctx.lineWidth = 1.5 * u
  ctx.stroke()

  // ---- AC tell: a small neutral roof vent, not a colour -- the 12M AC/NAC
  // distinction is shape now, same principle as 9M's shorter body.
  if (ac) {
    roundRect(ctx, cx - 10 * u, y0 + L * 0.42, 20 * u, 14 * u, 3 * u)
    ctx.fillStyle = 'rgba(255,255,255,0.55)'
    ctx.fill()
    ctx.strokeStyle = OUTLINE
    ctx.lineWidth = 1.5 * u
    ctx.stroke()
  }

  return ctx.getImageData(0, 0, size, size)
}
